/**
 * Cliente HTTP da aplicação.
 *
 * Fala sempre com `/api` no mesmo domínio — em desenvolvimento pelo proxy do
 * Vite, em produção pelo Traefik — para que o cookie de sessão seja sempre de
 * primeira parte e nunca precise ser lido pelo JavaScript.
 */

export class ErroDaApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    /** Corpo completo da resposta: alguns erros carregam contexto além da mensagem. */
    readonly corpo: Record<string, unknown> | null,
  ) {
    super(mensagem);
    this.name = 'ErroDaApi';
  }

  /** Problemas de validação por campo, quando a API os informou. */
  get problemas(): Array<{ campo: string; mensagem: string }> {
    const lista = this.corpo?.['problemas'];
    return Array.isArray(lista) ? (lista as Array<{ campo: string; mensagem: string }>) : [];
  }
}

type Opcoes = {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  corpo?: unknown;
  /** Impede a tentativa de renovar a sessão; usado pelas próprias rotas de sessão. */
  semRenovar?: boolean;
};

let renovacaoEmAndamento: Promise<boolean> | null = null;

async function renovarSessao(): Promise<boolean> {
  // Várias chamadas podem receber 401 ao mesmo tempo; uma única renovação
  // atende todas e evita invalidar o token recém-rotacionado.
  renovacaoEmAndamento ??= fetch('/api/autenticacao/renovar', {
    method: 'POST',
    credentials: 'same-origin',
  })
    .then((resposta) => resposta.ok)
    .catch(() => false)
    .finally(() => {
      renovacaoEmAndamento = null;
    });

  return renovacaoEmAndamento;
}

export async function chamarApi<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const executar = async (): Promise<Response> => {
    const corpo = opcoes.corpo;
    const formulario = corpo instanceof FormData;
    return fetch(`/api${caminho}`, {
      method: opcoes.metodo ?? 'GET',
      credentials: 'same-origin',
      // FormData precisa ir sem content-type definido por nós: o navegador
      // acrescenta o boundary do multipart.
      headers: corpo === undefined || formulario ? {} : { 'content-type': 'application/json' },
      body:
        corpo === undefined
          ? undefined
          : corpo instanceof FormData
            ? corpo
            : JSON.stringify(corpo),
    });
  };

  let resposta = await executar();

  if (resposta.status === 401 && !opcoes.semRenovar && (await renovarSessao())) {
    resposta = await executar();
  }

  if (!resposta.ok) {
    const corpo = (await resposta.json().catch(() => null)) as Record<string, unknown> | null;
    throw new ErroDaApi(
      resposta.status,
      typeof corpo?.['mensagem'] === 'string'
        ? corpo['mensagem']
        : 'Não foi possível concluir a operação.',
      corpo,
    );
  }

  if (resposta.status === 204) return undefined as T;
  const texto = await resposta.text();
  if (texto === '') return undefined as T;
  return JSON.parse(texto) as T;
}

/**
 * Traz um arquivo da API e entrega ao navegador.
 *
 * Não é um `<a href>` direto porque o endereço da API responde JSON quando
 * algo dá errado: um link cru levaria a pessoa para uma página com o erro em
 * vez de mostrá-lo na tela onde ela clicou. Passando por aqui, o 401 ainda
 * renova a sessão e o resto vira `ErroDaApi`, como em qualquer outra chamada.
 */
export async function baixarArquivo(caminho: string): Promise<void> {
  const executar = (): Promise<Response> =>
    fetch(`/api${caminho}`, { method: 'GET', credentials: 'same-origin' });

  let resposta = await executar();
  if (resposta.status === 401 && (await renovarSessao())) {
    resposta = await executar();
  }

  if (!resposta.ok) {
    const corpo = (await resposta.json().catch(() => null)) as Record<string, unknown> | null;
    throw new ErroDaApi(
      resposta.status,
      typeof corpo?.['mensagem'] === 'string'
        ? corpo['mensagem']
        : 'Não foi possível baixar o arquivo.',
      corpo,
    );
  }

  const endereco = URL.createObjectURL(await resposta.blob());
  const link = document.createElement('a');
  link.href = endereco;
  link.download = nomeDoAnexo(resposta.headers.get('content-disposition')) ?? 'arquivo';
  document.body.append(link);
  link.click();
  link.remove();
  // Revogar na hora seguinte, e não na mesma: o clique é assíncrono e o
  // navegador ainda está lendo o blob quando esta linha roda.
  setTimeout(() => URL.revokeObjectURL(endereco), 0);
}

/** O `filename` do cabeçalho de anexo, que é quem sabe o nome certo do arquivo. */
function nomeDoAnexo(cabecalho: string | null): string | null {
  const achado = cabecalho?.match(/filename="?([^";]+)"?/i);
  return achado?.[1] ?? null;
}
